import type {
  DocumentUploadDetails,
  FundingRequest,
  FundingRequestConversionResponse,
  FundingRequestCreationDetails,
  FundingRequestFundingStatus,
  FundingRequestLendingAmountUpdateDetails,
  FundingRequestLoanByLoanStatus,
  FundingRequestPage,
  FundingRequestResolveHelpRequest,
  FundingRequestReviewStatusDetails,
  FundingRequestStatusUpdateDetails,
  FundingRequestSubmitHelpRequest,
  FundingRequestTypeEnum,
  FundingRequestUpdateDetails,
  UploadUrlGenerationDetails,
  ZipDownloadRequest,
  DraftStdRequestStatus,
} from '@/funding-sdk';

import type { CreateUploadFileUrlInput, PagedParams, UpdateInput } from '@/types';
import BaseService, { type QueryParams } from './BaseService';

/** Wire JSON from GET /std-file/status (MRC snake_case attribute names). */
export interface StdFileStatusApiRow {
  description?: string;
  status_name?: string;
  status_code?: string;
  status_update_sak?: number;
  status_sak?: number;
  source_action_sak?: number;
  domainset_no?: number;
  mod_time?: string;
  mod_user?: string;
  create_time?: string;
  create_user?: string;
  is_complete?: string;
  comments?: string;
  is_complete_with_timeout?: string;
  action_name?: string;
  action_code?: string;
  action_order?: number;
}

export interface StdFileStatusApiResponse {
  /** Upload retry count from afp.mrc_fileupload (camelCase on the wire). */
  retryCount?: number;
  results?: StdFileStatusApiRow[];
}

/** Wire JSON from POST /std-file/{fundingRequestId}/retry-upload. */
export interface StdFileRetryUploadApiResponse {
  fundingRequestId?: number;
  mrcFileUploadId?: number;
  previousStatus?: string;
  status?: string;
  retryCount?: number;
  maxRetries?: number;
}

export interface MoveToNextStepInput extends UpdateInput {
  input?: FundingRequestStatusUpdateDetails;
}
export interface SubmitFundingRequestInput extends UpdateInput {
  input?: FundingRequestUpdateDetails;
}
export interface DeleteDocumentInput extends UpdateInput {
  documentId: number;
}

export interface UpdateFundingRequestInput extends UpdateInput {
  input: FundingRequestUpdateDetails;
}

export interface UpdateFundingRequestLendingAmountInput extends UpdateInput {
  input: FundingRequestLendingAmountUpdateDetails;
}

export interface UpdateFileStatusesInput extends UpdateInput {
  input: FundingRequestReviewStatusDetails;
}

export interface FundingRequestListParams extends PagedParams {
  collectionRecycling?: boolean;
  type?: FundingRequestTypeEnum;
}

export interface RequestHelpForFundingRequestInput extends UpdateInput {
  input?: FundingRequestSubmitHelpRequest;
}

export interface GenerateStdFilesForFundingRequestInput {
  id: number;
  version?: number;
}

export interface ResolveHelpRequestForFundingRequestInput extends UpdateInput {
  input?: FundingRequestResolveHelpRequest;
}

export type FundingRequestExportFormat = 'csv' | 'excel';

export interface FundingRequestSsrmExportRequest {
  filterModel?: Record<string, unknown> | null;
  advancedFilterModel?: Record<string, unknown> | null;
  sortModel?: unknown[];
}

export interface SendDocumentCorrectionEmailInput {
  id: number;
  version: number;
  input: {
    version: number;
    to: string[];
    cc?: string[];
    subject: string;
    bodyHtml: string;
    documentIds: number[];
    /** Misc / additional file document ids to omit from auto-attach on send (AD-3736). */
    excludedAdditionalDocumentIds?: number[];
    /** Approved sender DL → Funding → Notif fromAddress (OpenAPI 0.1.21) */
    fromAddress?: string;
  };
}

/** OpenAPI DraftEmailStatusEnum — interim until D.3-env SDK bump */
export type DraftEmailStatus = 'DRAFT' | 'SUBMITTED';

/** OpenAPI DraftEmailParentEntityTypeEnum */
export type DraftEmailParentEntityType = 'FUNDING_REQUEST';

/** OpenAPI DraftEmailKindEnum */
export type DraftEmailKind = 'DOCUMENT_CORRECTION';

/** OpenAPI DocumentSubmission (minimal) for draft PUT */
export type DraftEmailDocumentSubmission = {
  documentId: number;
};

/**
 * OpenAPI DraftEmail — GET/PUT response.
 * TODO D.3-env: replace with @/funding-sdk DraftEmail after funding SDK deploy + bump.
 */
export type DraftEmail = {
  id: number;
  status: DraftEmailStatus;
  parentEntityType: DraftEmailParentEntityType;
  parentEntityId: number;
  emailKind: DraftEmailKind;
  to: string[];
  cc?: string[];
  subject: string;
  bodyHtml: string;
  documents: Array<{ id: number; name: string; originalName?: string }>;
  notificationId?: number;
  submittedAt?: string;
  version: number;
  createdAt: string;
  updatedAt: string;
};

/** GET …/document-correction-email/compose-defaults */
export type DraftEmailComposeDefaults = {
  from: string;
  to: string[];
  cc: string[];
};

/** PUT …/document-correction-email/draft — mirrors OpenAPI DraftEmailSaveRequest */
export interface SaveDocumentCorrectionEmailDraftInput {
  id: number;
  /** Draft optimistic lock → X-Entity-Version; omit on first create */
  version?: number;
  input: {
    version?: number;
    to: string[];
    cc?: string[];
    subject: string;
    bodyHtml: string;
    /** OpenAPI documents (DocumentSubmission[]) — not Send’s documentIds */
    documents: DraftEmailDocumentSubmission[];
    /** Same From as compose FakeInput; Funding persists/forwards when wired */
    fromAddress?: string;
  };
}

/**
 * OpenAPI EditChecksSearchFileRoleEnum — interim until funding-sdk bump.
 */
export type EditChecksSearchFileRole = 'FUNDED_FILE' | 'SUPERSEDED';

/**
 * OpenAPI EditChecksCollateralSearchResult — interim until funding-sdk bump.
 */
export type EditChecksCollateralSearchResult = {
  fundingRequestId?: number;
  type?: FundingRequestTypeEnum;
  inputFileName?: string;
  submittedAt?: string;
  editCheckResult?: string;
  /** Collateral key history row_index — used to sort newest tapes first. */
  rowIndex: number;
  /** Number of loans in the job's parsed tape — shown in the Tape Row column. */
  tapeRows?: number;
  jobUuid: string;
  fileRole?: EditChecksSearchFileRole;
};

/**
 * OpenAPI EditChecksCollateralSearchResponse — interim until funding-sdk bump.
 */
export type EditChecksCollateralSearchResponse = {
  collateralKey: string;
  facility?: {
    facilityId?: string;
    name?: string;
    clientName?: string;
  };
  results: EditChecksCollateralSearchResult[];
};

class FundingRequestService extends BaseService {
  getById = async (id: string): Promise<FundingRequest> => {
    return this.get<FundingRequest>({ path: `fundingrequests/${id}` });
  };

  getList = async (params?: FundingRequestListParams | QueryParams): Promise<FundingRequestPage> => {
    const queryParams = params as QueryParams;
    return this.get<FundingRequestPage>({ path: 'fundingrequests', params: queryParams });
  };

  create = async (input: FundingRequestCreationDetails): Promise<FundingRequest> => {
    return this.post<FundingRequestCreationDetails, FundingRequest>({ path: 'fundingrequests', body: input });
  };

  save = async ({ id, version, input }: SubmitFundingRequestInput): Promise<FundingRequest> => {
    return this.put({ path: `fundingrequests/${id}/saved`, version, body: input });
  };

  submit = async ({ id, version, input }: SubmitFundingRequestInput): Promise<FundingRequest> => {
    return this.put({ path: `fundingrequests/${id}/state/submitted`, version, body: input });
  };

  moveToNextStep = async ({ id, version, input }: MoveToNextStepInput): Promise<FundingRequest> => {
    return this.patch({ path: `fundingrequests/${id}/state/next`, version, body: input });
  };

  regenerateFinalFundingFile = async ({ id, version }: UpdateInput): Promise<FundingRequest> => {
    return this.patch<void, FundingRequest>({ path: `fundingrequests/${id}/regenerateFinalFile`, version });
  };

  deleteRequest = async ({ id, version }: UpdateInput): Promise<null> => {
    return this.delete({ path: `fundingrequests/${id}`, version });
  };

  updateLendAmount = async ({
    input,
    id,
    version,
  }: UpdateFundingRequestLendingAmountInput): Promise<FundingRequest> => {
    return this.patch({ path: `fundingrequests/${id}/lendamount`, body: input, version });
  };

  // Preview lend amount breakdown for a given percentage of max
  getLendAmountPreview = async ({
    id,
    percentage,
    version,
  }: {
    id: number | string;
    percentage: number;
    version?: number;
  }): Promise<FundingRequestLoanByLoanStatus> => {
    return this.get<FundingRequestLoanByLoanStatus>({
      path: `fundingrequests/${id}/lendamount/preview`,
      params: { percentage },
      version,
    });
  };

  updateFileStatuses = async ({ id, input, version }: UpdateFileStatusesInput): Promise<FundingRequest> => {
    return this.patch<FundingRequestReviewStatusDetails, FundingRequest>({
      path: `fundingrequests/${id}/state/reviewed`,
      body: input,
      version,
    });
  };

  requestHelp = async ({ id, version, input }: RequestHelpForFundingRequestInput): Promise<FundingRequest> => {
    return this.post({ path: `fundingrequests/${id}/help`, version, body: input });
  };

  /** POST .../std-request — start on-demand draft STD generation (returns FundingRequest). */
  generateFundingRequestStdFiles = async ({
    id,
    version,
  }: GenerateStdFilesForFundingRequestInput): Promise<FundingRequest> => {
    return this.post({ path: `fundingrequests/${id}/std-request`, version });
  };

  /**
   * GET .../std-request — draft STD job status (DraftStdRequestStatus).
   * Needed in addition to FundingRequest.linkedDocuments: INTERNAL_STD_DRAFT / EXTERNAL_STD_DRAFT
   * rows only expose LinkedDocument.status OK|CORRECTION_REQUIRED, not IN_PROGRESS for a running job.
   */
  getFundingRequestStdFiles = async (id: number): Promise<DraftStdRequestStatus> => {
    return this.get({ path: `fundingrequests/${id}/std-request` });
  };

  /**
   * GET /std-file/status — ProMerit/MRC processing steps for a funding request
   * (AFR.GetProcessingStatus via funding service; MRC user comes from the auth principal).
   */
  getStdFileStatus = async ({
    fundingRequestId,
  }: {
    fundingRequestId: number | string;
  }): Promise<StdFileStatusApiResponse> => {
    return this.get<StdFileStatusApiResponse>({
      path: 'std-file/status',
      params: {
        fundingRequestId: String(fundingRequestId),
      },
    });
  };

  /**
   * POST /std-file/{fundingRequestId}/retry-upload — reset the STD upload row to NOT_STARTED so
   * MrcFileUploadJob picks it up again (retryCount cleared to 0 on the server).
   */
  retryStdFileUpload = async ({
    fundingRequestId,
  }: {
    fundingRequestId: number | string;
  }): Promise<StdFileRetryUploadApiResponse> => {
    return this.post<undefined, StdFileRetryUploadApiResponse>({
      path: `std-file/${fundingRequestId}/retry-upload`,
    });
  };

  resolveHelpRequest = async ({
    id,
    version,
    input,
  }: ResolveHelpRequestForFundingRequestInput): Promise<FundingRequest> => {
    return this.patch({ path: `fundingrequests/${id}/help`, version, body: input });
  };

  getStatus = async (id: number): Promise<FundingRequestFundingStatus> => {
    return this.get<FundingRequestFundingStatus>({ path: `fundingrequests/${id}/fundingstatus` });
  };

  cancelEditCheck = async ({ id, version }: UpdateInput): Promise<FundingRequest> => {
    return this.put({ path: `fundingrequests/${id}/edit-checks/cancel`, version });
  };

  exportFundingRequestsSsrm = async ({
    format = 'csv',
    request,
  }: {
    format?: FundingRequestExportFormat;
    request: FundingRequestSsrmExportRequest;
  }): Promise<Blob> => {
    const response = await this.post<FundingRequestSsrmExportRequest, Response>({
      path: 'fundingrequests/ssrm/export',
      params: { format },
      body: request,
      contentType: 'application/json',
    });

    if (response instanceof Response) {
      return response.blob();
    }

    throw new Error('Unexpected export response');
  };

  async startDownloadingFiles({ requestId }: { requestId: number }) {
    return this.post<ZipDownloadRequest, { processId: string }>({
      path: `fundingrequests/${requestId}/zip`,
    });
  }

  createUploadFileUrl = async ({ id, input }: CreateUploadFileUrlInput): Promise<DocumentUploadDetails> => {
    return this.post<UploadUrlGenerationDetails, DocumentUploadDetails>({
      path: `fundingrequests/${id}/document/url`,
      body: input,
    });
  };

  createDocumentCorrectionEmailUploadFileUrl = async ({
    id,
    input,
  }: CreateUploadFileUrlInput): Promise<DocumentUploadDetails> => {
    return this.post<UploadUrlGenerationDetails, DocumentUploadDetails>({
      path: `fundingrequests/${id}/document-correction-email/document/url`,
      body: input,
    });
  };

  convertPrefundingToFunding = async ({ id, version }: UpdateInput): Promise<FundingRequestConversionResponse> => {
    return this.post<void, FundingRequestConversionResponse>({ path: `fundingrequests/${id}/convert`, version });
  };

  sendDocumentCorrectionEmail = async ({ id, version, input }: SendDocumentCorrectionEmailInput): Promise<void> => {
    return this.post({
      path: `fundingrequests/${id}/document-correction-email/send`,
      version,
      body: input,
    });
  };

  getDocumentCorrectionEmailDraft = async (id: number): Promise<DraftEmail> => {
    return this.get<DraftEmail>({
      path: `fundingrequests/${id}/document-correction-email/draft`,
    });
  };

  getDocumentCorrectionEmailComposeDefaults = async (id: number): Promise<DraftEmailComposeDefaults> => {
    return this.get<DraftEmailComposeDefaults>({
      path: `fundingrequests/${id}/document-correction-email/compose-defaults`,
    });
  };

  saveDocumentCorrectionEmailDraft = async ({
    id,
    version,
    input,
  }: SaveDocumentCorrectionEmailDraftInput): Promise<DraftEmail> => {
    return this.put({
      path: `fundingrequests/${id}/document-correction-email/draft`,
      version,
      body: input,
    });
  };

  /**
   * GET /edit-checks/search?collateralKey=
   * Interim types until funding-sdk includes EditChecksCollateralSearchResponse.
   */
  searchEditChecksByCollateralKey = async (collateralKey: string): Promise<EditChecksCollateralSearchResponse> => {
    return this.get<EditChecksCollateralSearchResponse>({
      path: 'edit-checks/search',
      params: { collateralKey },
    });
  };
}

export default FundingRequestService;
